import { useState } from "react";
import {
  Box,
  Divider,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  Typography,
  useTheme,
} from "@mui/material";
import SelectAllIcon from "@mui/icons-material/TouchAppOutlined";
import CropSquareIcon from "@mui/icons-material/CropSquare";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CreateIcon from "@mui/icons-material/DrawOutlined";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import DeleteIcon from "@mui/icons-material/DeleteOutlineOutlined";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import { BlurOn } from "@mui/icons-material";
import HighlightIcon from "@mui/icons-material/BorderColor";
import CropIcon from "@mui/icons-material/Crop";
import LineWeightIcon from "@mui/icons-material/LineWeight";
import DocumentScannerIcon from "@mui/icons-material/DocumentScanner";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import FormatColorFillIcon from "@mui/icons-material/FormatColorFill";
import OpacityIcon from "@mui/icons-material/Opacity";
import { useTranslation } from "react-i18next";

import { Tool, BlurMode, TextFormatState } from "../types";
import {
  FONT_FAMILIES,
  MIN_FONT_SIZE,
  MAX_FONT_SIZE,
  FILL_COLORS,
} from "../constants";
import { FixedBottomToolbar } from "../styles";
import ColorPicker from "./ColorPicker";
import ThicknessControl from "./ThicknessControl";
import FillOpacityControl from "./FillOpacityControl";
import BlurControls from "./BlurControls";
import OcrControls from "./OcrControls";

interface AnnotationToolbarProps {
  activeTool: Tool;
  onToolSelect: (tool: Tool) => void;
  currentColor: string;
  onColorChange: (color: string) => void;
  shapeFillColor: string | null;
  onShapeFillColorChange: (color: string | null) => void;
  shapeFillOpacity: number;
  onShapeFillOpacityChange: (value: number) => void;
  showShapeControls: boolean;
  thickness: number;
  onThicknessChange: (value: number) => void;
  blurLevel: number;
  onBlurLevelChange: (value: number) => void;
  blurMode: BlurMode;
  onBlurModeChange: (mode: BlurMode) => void;
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  popupAbove: boolean;
  onOcrFullScreenshot: () => void;
  onOcrSelectRegion: () => void;
  isOcrProcessing: boolean;
  textFormat: TextFormatState;
  onTextFormatChange: (format: Partial<TextFormatState>) => void;
}

export default function Toolbar({
  activeTool,
  onToolSelect,
  currentColor,
  onColorChange,
  shapeFillColor,
  onShapeFillColorChange,
  shapeFillOpacity,
  onShapeFillOpacityChange,
  showShapeControls,
  thickness,
  onThicknessChange,
  blurLevel,
  onBlurLevelChange,
  blurMode,
  onBlurModeChange,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  popupAbove,
  onOcrFullScreenshot,
  onOcrSelectRegion,
  isOcrProcessing,
  textFormat,
  onTextFormatChange,
}: AnnotationToolbarProps) {
  const { t } = useTranslation(["bugherd"]);
  const theme = useTheme();
  const [openPopup, setOpenPopup] = useState<string | null>(null);

  const togglePopup = (id: string) => {
    setOpenPopup((prev) => (prev === id ? null : id));
  };

  const closeAllPopups = () => setOpenPopup(null);

  const handleToolClick = (toolId: Tool) => {
    onToolSelect(toolId);
    if (toolId === "blur") {
      setOpenPopup((prev) => (prev === "blur" ? null : "blur"));
    } else if (toolId !== "clear") {
      setOpenPopup(null);
    }
  };

  const isTextActive = activeTool === "text";
  const primaryColor = theme.palette.primary.main;
  const primaryHover = theme.palette.primary.dark;

  const toolBtnSx = (toolId: Tool) => ({
    color: activeTool === toolId ? "#fff" : "rgba(0, 0, 0, 0.6)",
    backgroundColor:
      activeTool === toolId ? primaryColor : "transparent",
    borderRadius: "8px",
    width: 36,
    height: 36,
    "&:hover": {
      backgroundColor: activeTool === toolId ? primaryHover : "rgba(0, 0, 0, 0.05)",
    },
  });

  const toggleBtnSx = (active: boolean) => ({
    color: active ? "#fff" : "rgba(0, 0, 0, 0.6)",
    backgroundColor: active ? primaryColor : "transparent",
    borderRadius: "8px",
    width: 36,
    height: 36,
    "&:hover": {
      backgroundColor: active ? primaryHover : "rgba(0, 0, 0, 0.05)",
    },
  });

  const placement = popupAbove ? "top" : ("bottom" as const);

  const DRAWING_TOOLS: { id: Tool; icon: React.ReactNode; tooltip: string }[] = [
    { id: "select", icon: <SelectAllIcon fontSize="small" />, tooltip: t("select") },
    { id: "arrow", icon: <ArrowForwardIcon fontSize="small" />, tooltip: t("arrow") },
    { id: "rectangle", icon: <CropSquareIcon fontSize="small" />, tooltip: t("rectangle") },
    { id: "circle", icon: <RadioButtonUncheckedIcon fontSize="small" />, tooltip: t("circle") },
    { id: "text", icon: <TextFieldsIcon fontSize="small" />, tooltip: t("text") },
    { id: "crop", icon: <CropIcon fontSize="small" />, tooltip: t("crop") },
    { id: "pencil", icon: <CreateIcon fontSize="small" />, tooltip: t("pencil") },
  ];

  const EFFECT_TOOLS: { id: Tool; icon: React.ReactNode; tooltip: string }[] = [
    { id: "blur", icon: <BlurOn fontSize="small" />, tooltip: t("blur") },
    { id: "highlight", icon: <HighlightIcon fontSize="small" />, tooltip: t("highlight") },
  ];

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        ...(popupAbove ? { pb: 2 } : { pt: 2 }),
        position: "relative",
      }}
    >
      <FixedBottomToolbar elevation={8} sx={{ position: "relative", borderRadius: "16px", width: "fit-content", maxWidth: "unset", flexWrap: "nowrap" }}>
        {/* ===== ROW 1: Tools + Text Format + Colors + OCR ===== */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexWrap: "nowrap", justifyContent: "center", whiteSpace: "nowrap" }}>
          {/* Drawing Tools */}
          {DRAWING_TOOLS.map((tool) => (
            <Tooltip key={tool.id} title={tool.tooltip} placement={placement}>
              <IconButton
                size="small"
                onClick={() => handleToolClick(tool.id)}
                sx={toolBtnSx(tool.id)}
              >
                {tool.icon}
              </IconButton>
            </Tooltip>
          ))}

          {/* Effect Tools */}
          {EFFECT_TOOLS.map((tool) => (
            <Box key={tool.id} sx={{ position: "relative" }}>
              <Tooltip title={tool.tooltip} placement={placement}>
                <IconButton
                  size="small"
                  onClick={() => handleToolClick(tool.id)}
                  sx={toolBtnSx(tool.id)}
                >
                  {tool.icon}
                </IconButton>
              </Tooltip>
              {tool.id === "blur" && openPopup === "blur" && activeTool === "blur" && (
                <BlurControls
                  blurLevel={blurLevel}
                  onBlurLevelChange={onBlurLevelChange}
                  blurMode={blurMode}
                  onBlurModeChange={onBlurModeChange}
                  popupAbove={popupAbove}
                />
              )}
            </Box>
          ))}

          {/* Divider before text format / controls */}
          <Divider orientation="vertical" flexItem sx={{ mx: 0.25, borderColor: "rgba(0,0,0,0.15)" }} />

          {/* === Text Format Controls (shown when text tool is active) === */}
          {isTextActive && (
            <>
              {/* Font Family Selector */}
              <Select
                value={textFormat.fontFamily}
                onChange={(e) => onTextFormatChange({ fontFamily: e.target.value })}
                size="small"
                variant="outlined"
                sx={{
                  height: 32,
                  fontSize: 12,
                  minWidth: 100,
                  "& .MuiOutlinedInput-notchedOutline": {
                    borderColor: "rgba(0,0,0,0.15)",
                    borderRadius: "6px",
                  },
                  "& .MuiSelect-select": { py: 0.5, px: 1 },
                }}
              >
                {FONT_FAMILIES.map((font) => (
                  <MenuItem key={font} value={font} sx={{ fontSize: 13, fontFamily: font }}>
                    {font}
                  </MenuItem>
                ))}
              </Select>

              <Divider orientation="vertical" flexItem sx={{ mx: 0.25, borderColor: "rgba(0,0,0,0.15)" }} />

              {/* Font Size: - SIZE + */}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
                <IconButton
                  size="small"
                  onClick={() =>
                    onTextFormatChange({
                      fontSize: Math.max(MIN_FONT_SIZE, textFormat.fontSize - 2),
                    })
                  }
                  sx={{ width: 28, height: 28 }}
                >
                  <RemoveIcon sx={{ fontSize: 16 }} />
                </IconButton>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    minWidth: 28,
                    textAlign: "center",
                    userSelect: "none",
                  }}
                >
                  {textFormat.fontSize}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() =>
                    onTextFormatChange({
                      fontSize: Math.min(MAX_FONT_SIZE, textFormat.fontSize + 2),
                    })
                  }
                  sx={{ width: 28, height: 28 }}
                >
                  <AddIcon sx={{ fontSize: 16 }} />
                </IconButton>
              </Box>

              {/* Bold */}
              <Tooltip title={t("bold", "Bold")} placement={placement}>
                <IconButton
                  size="small"
                  onClick={() => onTextFormatChange({ bold: !textFormat.bold })}
                  sx={toggleBtnSx(textFormat.bold)}
                >
                  <FormatBoldIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              {/* Italic */}
              <Tooltip title={t("italic", "Italic")} placement={placement}>
                <IconButton
                  size="small"
                  onClick={() => onTextFormatChange({ italic: !textFormat.italic })}
                  sx={toggleBtnSx(textFormat.italic)}
                >
                  <FormatItalicIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              {/* Underline */}
              <Tooltip title={t("underline", "Underline")} placement={placement}>
                <IconButton
                  size="small"
                  onClick={() => onTextFormatChange({ underline: !textFormat.underline })}
                  sx={toggleBtnSx(textFormat.underline)}
                >
                  <FormatUnderlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              <Divider orientation="vertical" flexItem sx={{ mx: 0.25, borderColor: "rgba(0,0,0,0.15)" }} />

              {/* Text Background Color (Fill) */}
              <Box sx={{ position: "relative" }}>
                <Tooltip title={t("backgroundColor", "Background Color")} placement={placement}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                      borderRadius: "8px",
                      px: 0.5,
                      py: 0.25,
                      "&:hover": { backgroundColor: "rgba(0,0,0,0.05)" },
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      togglePopup("textBg");
                    }}
                  >
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        backgroundColor: textFormat.backgroundColor || "#fff",
                        border: textFormat.backgroundColor
                          ? `2px solid ${textFormat.backgroundColor}`
                          : "1px solid #ccc",
                        position: "relative",
                        overflow: "hidden",
                      }}
                    >
                      {!textFormat.backgroundColor && (
                        <Box
                          sx={{
                            position: "absolute",
                            top: "50%",
                            left: "-10%",
                            width: "120%",
                            height: "2px",
                            backgroundColor: "#F44336",
                            transform: "rotate(-45deg)",
                            transformOrigin: "center",
                          }}
                        />
                      )}
                    </Box>
                    <ArrowDropDownIcon sx={{ fontSize: 16, color: "rgba(0,0,0,0.5)" }} />
                  </Box>
                </Tooltip>
                {openPopup === "textBg" && (
                  <ColorPicker
                    currentColor={textFormat.backgroundColor}
                    onColorChange={(c) => onTextFormatChange({ backgroundColor: c })}
                    onClose={closeAllPopups}
                    popupAbove={popupAbove}
                    colors={FILL_COLORS}
                  />
                )}
              </Box>

              {/* Text Color (Stroke) */}
              <Box sx={{ position: "relative" }}>
                <Tooltip title={t("textColor", "Text Color")} placement={placement}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      cursor: "pointer",
                      borderRadius: "8px",
                      px: 0.5,
                      py: 0.25,
                      "&:hover": { backgroundColor: "rgba(0,0,0,0.05)" },
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      togglePopup("textColor");
                    }}
                  >
                    <Box
                      sx={{
                        width: 24,
                        height: 24,
                        borderRadius: "50%",
                        backgroundColor: textFormat.textColor,
                        border: "1px solid rgba(0,0,0,0.2)",
                      }}
                    />
                    <ArrowDropDownIcon sx={{ fontSize: 16, color: "rgba(0,0,0,0.5)" }} />
                  </Box>
                </Tooltip>
                {openPopup === "textColor" && (
                  <ColorPicker
                    currentColor={textFormat.textColor}
                    onColorChange={(c) => c && onTextFormatChange({ textColor: c })}
                    onClose={closeAllPopups}
                    popupAbove={popupAbove}
                  />
                )}
              </Box>

              <Divider orientation="vertical" flexItem sx={{ mx: 0.25, borderColor: "rgba(0,0,0,0.15)" }} />
            </>
          )}

          {/* === Non-text controls (thickness, stroke color) shown when NOT text === */}
          {!isTextActive && (
            <>
              {/* Thickness Control */}
              <Box sx={{ position: "relative" }}>
                <Tooltip title={t("thickness")} placement={placement}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      togglePopup("thickness");
                    }}
                    sx={{ color: "rgba(0,0,0,0.6)", "&:hover": { backgroundColor: "rgba(0,0,0,0.05)" } }}
                  >
                    <LineWeightIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                {openPopup === "thickness" && (
                  <ThicknessControl
                    thickness={thickness}
                    onThicknessChange={onThicknessChange}
                    popupAbove={popupAbove}
                  />
                )}
              </Box>

              {/* Stroke Color */}
              <Box sx={{ position: "relative" }}>
                <Tooltip title={t("color", "Color")} placement={placement}>
                  <Box
                    sx={{
                      width: 28,
                      height: 28,
                      backgroundColor: currentColor,
                      borderRadius: "50%",
                      cursor: "pointer",
                      border: "2px solid #ccc",
                      "&:hover": { transform: "scale(1.1)" },
                    }}
                    onClick={(e) => {
                      e.stopPropagation();
                      togglePopup("color");
                    }}
                  />
                </Tooltip>
                {openPopup === "color" && (
                  <ColorPicker
                    currentColor={currentColor}
                    onColorChange={(c) => c && onColorChange(c)}
                    onClose={closeAllPopups}
                    popupAbove={popupAbove}
                  />
                )}
              </Box>

              {/* Shape Fill Color (rectangle/circle only) */}
              {showShapeControls && (
                <Box sx={{ position: "relative" }}>
                  <Tooltip title={t("fill", "Fill")} placement={placement}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        cursor: "pointer",
                        borderRadius: "8px",
                        px: 0.5,
                        py: 0.25,
                        "&:hover": { backgroundColor: "rgba(0,0,0,0.05)" },
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        togglePopup("shapeFill");
                      }}
                    >
                      <FormatColorFillIcon sx={{ fontSize: 18, color: "rgba(0,0,0,0.6)" }} />
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: "50%",
                          backgroundColor: shapeFillColor || "#fff",
                          border: shapeFillColor ? "1px solid rgba(0,0,0,0.2)" : "1px solid #ccc",
                          position: "relative",
                          overflow: "hidden",
                          ml: 0.5,
                        }}
                      >
                        {!shapeFillColor && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: "50%",
                              left: "-10%",
                              width: "120%",
                              height: "2px",
                              backgroundColor: "#F44336",
                              transform: "rotate(-45deg)",
                              transformOrigin: "center",
                            }}
                          />
                        )}
                      </Box>
                      <ArrowDropDownIcon sx={{ fontSize: 16, color: "rgba(0,0,0,0.5)" }} />
                    </Box>
                  </Tooltip>
                  {openPopup === "shapeFill" && (
                    <ColorPicker
                      currentColor={shapeFillColor}
                      onColorChange={onShapeFillColorChange}
                      onClose={closeAllPopups}
                      popupAbove={popupAbove}
                      colors={FILL_COLORS}
                    />
                  )}
                </Box>
              )}

              {/* Shape Fill Opacity (rectangle/circle only) */}
              {showShapeControls && (
                <Box sx={{ position: "relative" }}>
                  <Tooltip title={t("opacity", "Opacity")} placement={placement}>
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        togglePopup("shapeOpacity");
                      }}
                      sx={{ color: "rgba(0,0,0,0.6)", "&:hover": { backgroundColor: "rgba(0,0,0,0.05)" } }}
                    >
                      <OpacityIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  {openPopup === "shapeOpacity" && (
                    <FillOpacityControl
                      opacity={shapeFillOpacity}
                      onOpacityChange={onShapeFillOpacityChange}
                      popupAbove={popupAbove}
                    />
                  )}
                </Box>
              )}

              <Divider orientation="vertical" flexItem sx={{ mx: 0.25, borderColor: "rgba(0,0,0,0.15)" }} />
            </>
          )}

          {/* OCR */}
          <Box sx={{ position: "relative" }}>
            <Tooltip title={t("extractText")} placement={placement}>
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  togglePopup("ocr");
                }}
                sx={{ color: "rgba(0,0,0,0.6)", "&:hover": { backgroundColor: "rgba(0,0,0,0.05)" } }}
              >
                <DocumentScannerIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {openPopup === "ocr" && (
              <OcrControls
                onFullScreenshot={() => {
                  closeAllPopups();
                  onOcrFullScreenshot();
                }}
                onSelectRegion={() => {
                  closeAllPopups();
                  onOcrSelectRegion();
                }}
                isProcessing={isOcrProcessing}
                popupAbove={popupAbove}
              />
            )}
          </Box>

          {/* Divider before undo/redo/delete */}
          <Divider orientation="vertical" flexItem sx={{ mx: 0.25, borderColor: "rgba(0,0,0,0.15)" }} />

          {/* Undo / Redo / Delete */}
          <Tooltip title={t("Undo")} placement={placement}>
            <span>
              <IconButton
                size="small"
                onClick={onUndo}
                disabled={!canUndo}
                sx={{
                  color: !canUndo ? "rgba(0,0,0,0.3)" : "rgba(0,0,0,0.6)",
                  "&:hover": { backgroundColor: "rgba(0,0,0,0.05)" },
                  "&.Mui-disabled": { color: "rgba(0,0,0,0.3)" },
                }}
              >
                <UndoIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t("Redo")} placement={placement}>
            <span>
              <IconButton
                size="small"
                onClick={onRedo}
                disabled={!canRedo}
                sx={{
                  color: !canRedo ? "rgba(0,0,0,0.3)" : "rgba(0,0,0,0.6)",
                  "&:hover": { backgroundColor: "rgba(0,0,0,0.05)" },
                  "&.Mui-disabled": { color: "rgba(0,0,0,0.3)" },
                }}
              >
                <RedoIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t("clearAll")} placement={placement}>
            <IconButton
              size="small"
              onClick={() => handleToolClick("clear")}
              sx={{ color: "rgba(0,0,0,0.6)", "&:hover": { backgroundColor: "rgba(0,0,0,0.05)" } }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </FixedBottomToolbar>
    </Box>
  );
}
